import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { PhoneInput } from '../ui/phone-input';
import { ArrowLeft, Save, Loader2, User, Briefcase, CreditCard, Phone, AlertCircle, Lock, DollarSign, Plus } from 'lucide-react';
import { employeeApi, departmentApi, settingsApi } from '../../services/api';
import { EmployeeStatus } from '../../types/hr';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { AddSkillDialog } from './AddSkillDialog';

// Validation helpers
const validateEmail = (email: string): boolean => {
  if (!email) return true; // Empty is valid (optional field)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  if (!phone) return true; // Empty is valid (optional field)
  // Allow various formats: +1-234-567-8900, (234) 567-8900, 234.567.8900, 2345678900, etc.
  const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

interface ValidationErrors {
  work_email?: string;
  personal_email?: string;
  work_phone?: string;
  personal_phone?: string;
  emergency_contact_phone?: string;
}

interface EmployeeFormData {
  // Basic Info
  first_name: string;
  last_name: string;
  job_title: string;
  work_email: string;
  work_phone: string;
  company: string;
  department_id: string;
  status: EmployeeStatus;
  avatar_url: string;
  // Login invitation (only for new employees)
  login_email: string;
  login_role_id: string;
  // Personal Information
  date_of_birth: string;
  gender: string;
  marital_status: string;
  nationality: string;
  personal_email: string;
  personal_phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  // Work Information
  employee_number: string;
  employment_type: string;
  hire_date: string;
  probation_end_date: string;
  office_location: string;
  work_schedule: string;
  manager_id: string;
  // Compensation
  base_salary: string;
  allowances: string;
  bank_name: string;
  bank_account: string;
  routing_number: string;
  account_type: string;
}

const initialFormData: EmployeeFormData = {
  first_name: '',
  last_name: '',
  job_title: '',
  work_email: '',
  work_phone: '',
  company: '',
  department_id: '',
  status: EmployeeStatus.ACTIVE,
  avatar_url: '',
  login_email: '',
  login_role_id: '',
  date_of_birth: '',
  gender: '',
  marital_status: '',
  nationality: '',
  personal_email: '',
  personal_phone: '',
  address: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
  employee_number: '',
  employment_type: '',
  hire_date: '',
  probation_end_date: '',
  office_location: '',
  work_schedule: '',
  manager_id: '',
  base_salary: '',
  allowances: '',
  bank_name: '',
  bank_account: '',
  routing_number: '',
  account_type: '',
};

const EmployeeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<EmployeeFormData>(initialFormData);
  const [activeTab, setActiveTab] = useState('basic');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showSkillPrompt, setShowSkillPrompt] = useState(false);
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [createdEmployeeId, setCreatedEmployeeId] = useState<string | null>(null);

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [resetSuccessData, setResetSuccessData] = useState<{ temporary_password: string } | null>(null);

  const resetPasswordMutation = useMutation({
    mutationFn: (employeeId: string) => employeeApi.resetPassword(employeeId),
    onSuccess: (data) => {
      setIsResetConfirmOpen(false);
      setResetSuccessData(data);
      toast({
        title: 'Password Reset Successful',
        description: 'A new temporary password has been generated.',
      });
    },
    onError: (error: Error) => {
      setIsResetConfirmOpen(false);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset password',
        variant: 'destructive',
      });
    },
  });

  // Company is tenant-owned. Employees created in HR should always inherit the
  // current tenant company instead of accepting a free-form company name.
  const { data: systemSettings = [] } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: () => settingsApi.system.getAll(),
  });

  const companySetting = systemSettings.find((setting: any) => setting.category === 'company');
  const companyInfo = companySetting?.setting_value
    ? typeof companySetting.setting_value === 'string'
      ? JSON.parse(companySetting.setting_value)
      : companySetting.setting_value
    : null;
  const currentCompanyName = companyInfo?.company_name || companyInfo?.legal_name || '';

  // Validate a field and update errors
  const validateField = (field: keyof ValidationErrors, value: string) => {
    let error = '';

    if (field === 'work_email' || field === 'personal_email') {
      if (value && !validateEmail(value)) {
        error = 'Please enter a valid email address';
      }
    } else if (field === 'work_phone' || field === 'personal_phone' || field === 'emergency_contact_phone') {
      if (value && !validatePhone(value)) {
        error = 'Please enter a valid phone number';
      }
    }

    setValidationErrors(prev => ({
      ...prev,
      [field]: error || undefined,
    }));

    return !error;
  };

  // Validate all fields before submit
  const validateAllFields = (): boolean => {
    const errors: ValidationErrors = {};

    if (formData.work_email && !validateEmail(formData.work_email)) {
      errors.work_email = 'Please enter a valid email address';
    }
    if (formData.personal_email && !validateEmail(formData.personal_email)) {
      errors.personal_email = 'Please enter a valid email address';
    }
    if (formData.work_phone && !validatePhone(formData.work_phone)) {
      errors.work_phone = 'Please enter a valid phone number';
    }
    if (formData.personal_phone && !validatePhone(formData.personal_phone)) {
      errors.personal_phone = 'Please enter a valid phone number';
    }
    if (formData.emergency_contact_phone && !validatePhone(formData.emergency_contact_phone)) {
      errors.emergency_contact_phone = 'Please enter a valid phone number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch employee data if editing
  const { data: employee, isLoading: employeeLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeeApi.getById(id!),
    enabled: isEditMode,
  });

  // Fetch departments for dropdown
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll(),
  });

  // Fetch roles for dropdown
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => employeeApi.getRoles(),
  });

  // Fetch manager employees for manager dropdown (employees who have MANAGER/ADMIN role)
  const { data: managers = [] } = useQuery({
    queryKey: ['managers'],
    queryFn: async () => {
      const response = await fetch('/api/employees/managers', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch managers');
      }
      return response.json();
    },
  });

  // Populate form when employee data is loaded
  useEffect(() => {
    if (employee && isEditMode) {
      setFormData({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        job_title: employee.job_title || '',
        work_email: employee.work_email || '',
        work_phone: employee.work_phone || '',
        company: currentCompanyName || employee.company || '',
        department_id: employee.department_id || '',
        status: employee.status as EmployeeStatus || EmployeeStatus.ACTIVE,
        avatar_url: employee.avatar_url || '',
        login_email: '',
        login_role_id: '',
        date_of_birth: employee.date_of_birth || '',
        gender: employee.gender || '',
        marital_status: employee.marital_status || '',
        nationality: employee.nationality || '',
        personal_email: employee.personal_email || '',
        personal_phone: employee.personal_phone || '',
        address: employee.address || '',
        city: employee.city || '',
        state: employee.state || '',
        country: employee.country || '',
        postal_code: employee.postal_code || '',
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        emergency_contact_relationship: employee.emergency_contact_relationship || '',
        employee_number: employee.employee_number || '',
        employment_type: employee.employment_type || '',
        hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
        probation_end_date: employee.probation_end_date ? employee.probation_end_date.split('T')[0] : '',
        office_location: employee.office_location || '',
        work_schedule: employee.work_schedule || '',
        manager_id: employee.manager_id || '',
        base_salary: employee.base_salary?.toString() || '',
        allowances: employee.allowances?.toString() || '',
        bank_name: employee.bank_name || '',
        bank_account: employee.bank_account || '',
        routing_number: employee.routing_number || '',
        account_type: employee.account_type || '',
      });
    }
  }, [employee, isEditMode, currentCompanyName]);

  useEffect(() => {
    if (!isEditMode && currentCompanyName) {
      setFormData(prev => ({ ...prev, company: currentCompanyName }));
    }
  }, [currentCompanyName, isEditMode]);

  // Create employee mutation
  const createMutation = useMutation({
    mutationFn: employeeApi.create,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast({ title: 'Employee created successfully' });
      setCreatedEmployeeId(data.id);
      setShowSkillPrompt(true);
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating employee', description: error.message, variant: 'destructive' });
    },
  });

  // Update employee mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => employeeApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      toast({ title: 'Employee updated successfully' });
      navigate(`/employees/${id}`);
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating employee', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) {
      toast({ title: 'Please fill in required fields (First Name, Last Name)', variant: 'destructive' });
      return;
    }

    // For new employees, invite email is required. Password setup is handled by Cognito/Dealbox signup.
    if (!isEditMode) {
      if (!formData.login_email && !formData.work_email) {
        toast({ title: 'Please provide a login email or work email', variant: 'destructive' });
        return;
      }
    }

    // Validate email and phone fields
    if (!validateAllFields()) {
      toast({ title: 'Please fix validation errors before submitting', variant: 'destructive' });
      return;
    }

    // Prepare data, converting string numbers to actual numbers
    const submitData: any = {
      ...formData,
      company: currentCompanyName || formData.company,
      base_salary: formData.base_salary ? parseFloat(formData.base_salary) : undefined,
      allowances: formData.allowances ? parseFloat(formData.allowances) : undefined,
    };

    // Include login credentials for new employees
    if (!isEditMode) {
      submitData.login_email = formData.login_email || formData.work_email;
      delete submitData.login_password;
    }

    // Remove empty strings - convert to undefined
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '') {
        submitData[key] = undefined;
      }
    });

    if (isEditMode) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleInputChange = (field: keyof EmployeeFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate on change for validation-enabled fields
    if (['work_email', 'personal_email', 'work_phone', 'personal_phone', 'emergency_contact_phone'].includes(field)) {
      validateField(field as keyof ValidationErrors, value);
    }
  };

  // Render validation error message
  const renderError = (field: keyof ValidationErrors) => {
    if (validationErrors[field]) {
      return (
        <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
          <AlertCircle className="h-3 w-3" />
          {validationErrors[field]}
        </p>
      );
    }
    return null;
  };

  if (isEditMode && employeeLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading employee data...</span>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-50/30">
      {/* Header - Fixed */}
      <div className="px-8 py-6 bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 shadow-sm z-20 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(isEditMode ? `/employees/${id}` : '/employees')} 
            className="h-10 w-10 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-sm transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#143A62] dark:text-slate-100">
              {isEditMode ? 'Edit Employee' : 'Add New Employee'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[12px] font-bold text-slate-400">Directory</span>
              <span className="text-slate-300 dark:text-slate-600 text-xs">/</span>
              <span className="text-[12px] font-bold text-blue-500">{isEditMode ? 'Edit Profile' : 'New Profile'}</span>
            </div>
          </div>
        </div>
        
        {isEditMode && (
          <Button
            type="button"
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/50"
            onClick={() => setIsResetConfirmOpen(true)}
          >
            <Lock className="w-4 h-4 mr-2" />
            Reset Password
          </Button>
        )}
      </div>

      {/* Form with Tabs and internal scrolling */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-8 py-4 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border-b border-slate-100 dark:border-slate-800">
            <TabsList className="bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-1 w-fit shadow-sm backdrop-blur-sm">
              <TabsTrigger value="basic" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
                <User className="h-4 w-4 mr-2" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="personal" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
                <Phone className="h-4 w-4 mr-2" />
                Personal
              </TabsTrigger>
              <TabsTrigger value="work" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
                <Briefcase className="h-4 w-4 mr-2" />
                Work Info
              </TabsTrigger>
              <TabsTrigger value="compensation" className="px-6 py-2 text-sm font-bold data-[state=active]:bg-[#143A62] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600 transition-all rounded-md">
                <CreditCard className="h-4 w-4 mr-2" />
                Compensation
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar bg-white/70 backdrop-blur-md dark:bg-slate-900/70">
            <div className="max-w-6xl mx-auto p-10">
          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-10 focus-visible:outline-none m-0">
                <div className="flex justify-center pb-10 border-b border-slate-100 dark:border-slate-800">
                  <ProfilePhotoUpload
                    currentPhotoUrl={formData.avatar_url || undefined}
                    initials={`${formData.first_name?.charAt(0) || 'N'}${formData.last_name?.charAt(0) || 'N'}`}
                    onPhotoChange={(url) => handleInputChange('avatar_url', url || '')}
                    size="lg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">First Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      placeholder="e.g. John"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      placeholder="e.g. Doe"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Job Title</Label>
                    <Input
                      id="job_title"
                      value={formData.job_title}
                      onChange={(e) => handleInputChange('job_title', e.target.value)}
                      placeholder="e.g. Software Engineer"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Work Email</Label>
                    <Input
                      id="work_email"
                      type="email"
                      value={formData.work_email}
                      onChange={(e) => handleInputChange('work_email', e.target.value)}
                      placeholder="john.doe@company.com"
                      className={`bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 ${validationErrors.work_email ? 'border-red-500' : ''}`}
                    />
                    {renderError('work_email')}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Work Phone</Label>
                    <PhoneInput
                      id="work_phone"
                      value={formData.work_phone}
                      onChange={(e) => handleInputChange('work_phone', e)}
                      placeholder="(234) 567-8900"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                    {renderError('work_phone')}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      readOnly
                      disabled
                      placeholder="Company is set from your tenant"
                      className="bg-gray-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 text-slate-600 cursor-not-allowed"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Department</Label>
                    <Select 
                      key={`dept-select-${formData.department_id || 'none'}`}
                      value={formData.department_id || ""} 
                      onValueChange={(value) => handleInputChange('department_id', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select department">
                          {formData.department_id ? departments.find(d => d.id === formData.department_id)?.name : "Select department"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        {departments.map(dept => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value as EmployeeStatus)}>
                      <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        <SelectItem value={EmployeeStatus.ACTIVE}>Active</SelectItem>
                        <SelectItem value={EmployeeStatus.ONBOARDING}>Onboarding</SelectItem>
                        <SelectItem value={EmployeeStatus.OFFBOARDING}>Offboarding</SelectItem>
                        <SelectItem value={EmployeeStatus.INACTIVE}>Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Login Invitation Section - Only show when creating new employee */}
                {!isEditMode && (
                  <div className="mt-8 pt-8 border-t border-gray-100 dark:border-slate-800">
                    <div className="flex items-center gap-2 mb-4">
                      <Lock className="h-4 w-4 text-blue-500" />
                      <h3 className="text-lg font-bold text-[#143A62] dark:text-slate-100">Login Invitation</h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
                      The employee will receive a Dealbox invitation and set their own password. The system will use the work email if a login email is not specified.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Login Email</Label>
                        <Input
                          id="login_email"
                          type="email"
                          value={formData.login_email}
                          onChange={(e) => handleInputChange('login_email', e.target.value)}
                          placeholder="employee@company.com"
                          className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">System Role</Label>
                        <Select value={formData.login_role_id} onValueChange={(value) => handleInputChange('login_role_id', value)}>
                          <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                            {roles.map((role: any) => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
          </TabsContent>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-10 focus-visible:outline-none m-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Date of Birth</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Gender</Label>
                    <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                      <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Marital Status</Label>
                    <Select value={formData.marital_status} onValueChange={(value) => handleInputChange('marital_status', value)}>
                      <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                        <SelectValue placeholder="Select marital status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                        <SelectItem value="Single">Single</SelectItem>
                        <SelectItem value="Married">Married</SelectItem>
                        <SelectItem value="Divorced">Divorced</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nationality</Label>
                    <Input
                      value={formData.nationality}
                      onChange={(e) => handleInputChange('nationality', e.target.value)}
                      placeholder="e.g. American"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Personal Email</Label>
                    <Input
                      type="email"
                      value={formData.personal_email}
                      onChange={(e) => handleInputChange('personal_email', e.target.value)}
                      placeholder="e.g. john.doe@gmail.com"
                      className={`bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 ${validationErrors.personal_email ? 'border-red-500' : ''}`}
                    />
                    {renderError('personal_email')}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Personal Phone</Label>
                    <PhoneInput
                      value={formData.personal_phone}
                      onChange={(e) => handleInputChange('personal_phone', e)}
                      placeholder="(234) 567-8900"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                    {renderError('personal_phone')}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Full Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter street address, building, etc."
                    rows={3}
                    className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="City"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="State"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Country</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      placeholder="Country"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Postal Code</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="Zip"
                      className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                    />
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="mt-8 pt-8 border-t border-gray-100 dark:border-slate-800">
                  <div className="flex items-center gap-2 mb-6">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <h3 className="text-lg font-bold text-[#143A62] dark:text-slate-100">Emergency Contact</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Contact Name</Label>
                      <Input
                        value={formData.emergency_contact_name}
                        onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                        placeholder="Full name"
                        className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Contact Phone</Label>
                      <PhoneInput
                        value={formData.emergency_contact_phone}
                        onChange={(e) => handleInputChange('emergency_contact_phone', e)}
                        placeholder="(234) 567-8900"
                        className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                      />
                      {renderError('emergency_contact_phone')}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Relationship</Label>
                      <Select value={formData.emergency_contact_relationship} onValueChange={(value) => handleInputChange('emergency_contact_relationship', value)}>
                        <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                          <SelectItem value="Spouse">Spouse</SelectItem>
                          <SelectItem value="Parent">Parent</SelectItem>
                          <SelectItem value="Sibling">Sibling</SelectItem>
                          <SelectItem value="Child">Child</SelectItem>
                          <SelectItem value="Friend">Friend</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
          </TabsContent>

          {/* Work Information Tab */}
          {/* Work Information Tab */}
          <TabsContent value="work" className="space-y-10 focus-visible:outline-none m-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Employee Number</Label>
                <Input
                  value={formData.employee_number}
                  onChange={(e) => handleInputChange('employee_number', e.target.value)}
                  placeholder="e.g. EMP001"
                  className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Employment Type</Label>
                <Select value={formData.employment_type} onValueChange={(value) => handleInputChange('employment_type', value)}>
                  <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Intern">Intern</SelectItem>
                    <SelectItem value="Freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Hire Date</Label>
                <Input
                  type="date"
                  value={formData.hire_date}
                  onChange={(e) => handleInputChange('hire_date', e.target.value)}
                  className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Probation End Date</Label>
                <Input
                  type="date"
                  value={formData.probation_end_date}
                  onChange={(e) => handleInputChange('probation_end_date', e.target.value)}
                  className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Office Location</Label>
                <Input
                  value={formData.office_location}
                  onChange={(e) => handleInputChange('office_location', e.target.value)}
                  placeholder="e.g. Headquarters"
                  className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Work Schedule</Label>
                <Select value={formData.work_schedule} onValueChange={(value) => handleInputChange('work_schedule', value)}>
                  <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                    <SelectValue placeholder="Select schedule" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <SelectItem value="9 AM - 5 PM">9 AM - 5 PM</SelectItem>
                    <SelectItem value="8 AM - 4 PM">8 AM - 4 PM</SelectItem>
                    <SelectItem value="10 AM - 6 PM">10 AM - 6 PM</SelectItem>
                    <SelectItem value="Flexible">Flexible</SelectItem>
                    <SelectItem value="Shift-based">Shift-based</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Reports To (Manager)</Label>
                {user?.role === 'ADMIN' ? (
                  <Select value={formData.manager_id} onValueChange={(value) => handleInputChange('manager_id', value)}>
                    <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 text-sm">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      {managers
                        .filter((mgr: any) => mgr.id !== id) // Exclude current employee
                        .map((mgr: any) => (
                          <SelectItem key={mgr.id} value={mgr.id}>
                            {mgr.first_name} {mgr.last_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="px-4 py-2 border rounded-md bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm">
                    <p className="text-slate-600 dark:text-slate-400 font-medium">
                      {formData.manager_id ? (
                        (() => {
                          const manager = managers.find((m: any) => m.id === formData.manager_id);
                          return manager
                            ? `${manager.first_name} ${manager.last_name}`
                            : 'No manager assigned';
                        })()
                      ) : (
                        'No manager assigned'
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Compensation Tab */}
          <TabsContent value="compensation" className="space-y-10 focus-visible:outline-none m-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Base Salary (Annual)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    value={formData.base_salary}
                    onChange={(e) => handleInputChange('base_salary', e.target.value)}
                    placeholder="0.00"
                    className="pl-9 bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Allowances (Annual)</Label>
                <div className="relative">
                  <Plus className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    value={formData.allowances}
                    onChange={(e) => handleInputChange('allowances', e.target.value)}
                    placeholder="0.00"
                    className="pl-9 bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="mt-8 pt-8 border-t border-gray-100 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-6">
                <CreditCard className="h-4 w-4 text-emerald-500" />
                <h3 className="text-lg font-bold text-[#143A62] dark:text-slate-100">Banking Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Bank Name</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    placeholder="e.g. Bank of America"
                    className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Account Number</Label>
                  <Input
                    value={formData.bank_account}
                    onChange={(e) => handleInputChange('bank_account', e.target.value)}
                    placeholder="e.g. 1234567890"
                    className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Routing Number</Label>
                  <Input
                    value={formData.routing_number}
                    onChange={(e) => handleInputChange('routing_number', e.target.value)}
                    placeholder="e.g. 098765432"
                    className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Account Type</Label>
                  <Select value={formData.account_type} onValueChange={(value) => handleInputChange('account_type', value)}>
                    <SelectTrigger className="bg-gray-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      <SelectItem value="Checking">Checking</SelectItem>
                      <SelectItem value="Savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

          </TabsContent>

          {/* Form Actions - Integrated into scroll view */}
          <div className="mt-12 pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEditMode ? `/employees/${id}` : '/employees')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Update Employee' : 'Create Employee'}
                </>
              )}
            </Button>
          </div>

        </div>
      </div>
    </Tabs>
  </form>

  {/* Reset Password Confirmation Dialog */}
  <Dialog open={isResetConfirmOpen} onOpenChange={setIsResetConfirmOpen}>
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Reset Employee Password</DialogTitle>
        <DialogDescription>
          Are you sure you want to reset the password for {formData.first_name} {formData.last_name}? 
          This will immediately invalidate their current password and generate a new temporary password.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => setIsResetConfirmOpen(false)}>Cancel</Button>
        <Button 
          variant="destructive" 
          onClick={() => {
            if (id) resetPasswordMutation.mutate(id);
          }}
          disabled={resetPasswordMutation.isPending}
        >
          {resetPasswordMutation.isPending ? 'Resetting...' : 'Yes, Reset Password'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  {/* Reset Password Success Dialog */}
  <Dialog open={!!resetSuccessData} onOpenChange={(open) => {
    if (!open) setResetSuccessData(null);
  }}>
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle className="flex items-center text-green-600">
          <AlertCircle className="w-5 h-5 mr-2" />
          Password Reset Complete
        </DialogTitle>
        <DialogDescription>
          A new temporary password has been generated. Please securely share this with the employee. They will be forced to change it upon their next login.
        </DialogDescription>
      </DialogHeader>
      
      <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md border border-slate-200 dark:border-slate-800 mt-4 text-center">
        <span className="text-2xl font-mono tracking-widest font-bold text-slate-800 dark:text-slate-200">
          {resetSuccessData?.temporary_password}
        </span>
      </div>

      <DialogFooter className="mt-4">
        <Button 
          className="w-full"
          onClick={() => {
            if (resetSuccessData?.temporary_password) {
              navigator.clipboard.writeText(resetSuccessData.temporary_password);
              toast({ title: "Copied to clipboard!" });
            }
          }}
        >
          Copy to Clipboard
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  {/* Skill Prompt Dialog */}
  <Dialog open={showSkillPrompt} onOpenChange={(open) => {
    if (!open) {
      setShowSkillPrompt(false);
      navigate('/employees');
    }
  }}>
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Add Employee Skills</DialogTitle>
        <DialogDescription>
          Employee has been successfully created. Do you want to add skills for this employee now?
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex gap-2 sm:justify-end">
        <Button 
          variant="outline" 
          onClick={() => {
            setShowSkillPrompt(false);
            navigate('/employees');
          }}
        >
          No, later
        </Button>
        <Button 
          onClick={() => {
            setShowSkillPrompt(false);
            setIsAddSkillOpen(true);
          }}
        >
          Yes, add skills
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  {/* Actual Add Skill Dialog shown if user clicks yes */}
  {createdEmployeeId && (
    <AddSkillDialog 
      employeeId={createdEmployeeId} 
      isOpen={isAddSkillOpen} 
      onClose={() => navigate('/employees')} 
    />
  )}
</div>
  );
};

export default EmployeeForm;
